// Placed models in the floor plan. Each placement = a transform group (its
// baked mesh clone + position on the floor, yaw, uniform scale). Stores only
// modelId + transform (the mesh comes from bakeModelClone), so it's light.

import * as THREE from "three";
import { applyBakeLook } from "./model-bake";
import type { SavedPlacement } from "./saved-floor-plan";
import { markFloorMutation } from "./history";

export interface PlacementInfo {
  id: number;
  modelId: string;
  rotationDeg: number; // yaw degrees
  scale: number; // uniform
}

interface Entry {
  id: number;
  modelId: string;
  group: THREE.Group; // position (floor) + rotation.y + scale
  // Footprint outline (the object's vertical mesh faces projected to XZ) in
  // group-LOCAL coords, computed once and reused as measure-snap targets.
  footprint?: { ax: number; az: number; bx: number; bz: number }[];
  // Grouping: a label shared by every member of one group (move/rotate/select as
  // a unit). Absent when the model isn't grouped.
  groupId?: number;
  // Per-model dimension-visibility override (set via the right-click menu):
  //   undefined → follow the global "Model dimensions" toggle
  //   true       → always show this model's dims (even with the toggle off)
  //   false      → always hide this model's dims (even with the toggle on)
  dimsShow?: boolean;
  // Per-model hide (right-click "Hide model"). A hidden model is fully invisible
  // and not selectable, but still appears as a faint translucent ghost on hover
  // so you can right-click → "Show model". Persisted; the global "Models" toggle
  // off→on un-hides everything (see showAll). Distinct from setVisible, which
  // hides ALL models at once without touching this per-model flag.
  hidden?: boolean;
  // Transient (never serialized) render state for the hover ghost: while ghosted
  // we swap each mesh to a translucent clone (the bake's materials are SHARED
  // across every placement of a model, so we must clone — never mutate them).
  // The edge lines stay visible so the hover white-edge outline still applies.
  ghosted?: boolean;
  ghostMats?: { mesh: THREE.Mesh; mat: THREE.Material | THREE.Material[] }[];
}

export class PlacementSystem {
  readonly group = new THREE.Group();
  private items = new Map<number, Entry>();
  private next = 1;
  private nextGroup = 1;
  private selected: number | null = null;
  private presentation = false;

  // Show/hide every placed model at once (hidden models are also non-pickable,
  // since three skips invisible objects when raycasting).
  setVisible(v: boolean): void {
    this.group.visible = v;
  }

  add(modelId: string, mesh: THREE.Object3D, x: number, z: number): number {
    markFloorMutation();
    const id = this.next++;
    const g = new THREE.Group();
    g.position.set(x, 0, z);
    g.userData.placementId = id;
    mesh.traverse((o) => applyBakeLook(o, this.presentation)); // match the active look
    g.add(mesh);
    this.group.add(g);
    this.items.set(id, { id, modelId, group: g });
    return id;
  }

  // Presentation lighting rig: swap every placement between the bake's flat
  // editing look and its per-voxel AO look (see model-bake's applyBakeLook).
  setPresentation(on: boolean): void {
    if (this.presentation === on) return;
    this.presentation = on;
    this.group.traverse((o) => applyBakeLook(o, on));
  }

  // Distinct model ids that currently have at least one placement (for on-demand
  // AO when presentation lighting turns on).
  placedModelIds(): string[] {
    const ids = new Set<string>();
    for (const e of this.items.values()) ids.add(e.modelId);
    return [...ids];
  }

  remove(id: number): void {
    markFloorMutation();
    const e = this.items.get(id);
    if (!e) return;
    this.group.remove(e.group);
    this.items.delete(id);
    if (this.selected === id) this.setSelected(null);
    this.pruneGroups(); // a group left with one member is no longer a group
  }

  // ── Grouping ───────────────────────────────────────────────────────────────
  // Members of `id`'s group (including `id`); just [id] when it isn't grouped.
  groupSiblings(id: number): number[] {
    const gid = this.items.get(id)?.groupId;
    if (gid == null) return this.items.has(id) ? [id] : [];
    const out: number[] = [];
    for (const e of this.items.values()) if (e.groupId === gid) out.push(e.id);
    return out;
  }
  isGrouped(id: number): boolean {
    return this.items.get(id)?.groupId != null;
  }
  // Every distinct group, as a list of its member ids (each group has ≥2).
  groups(): number[][] {
    const byGid = new Map<number, number[]>();
    for (const e of this.items.values()) {
      if (e.groupId == null) continue;
      const arr = byGid.get(e.groupId) ?? [];
      arr.push(e.id);
      byGid.set(e.groupId, arr);
    }
    return [...byGid.values()];
  }
  // Merge the given ids (and anything already grouped with them) into ONE new
  // group. Returns the new group id, or null if fewer than 2 distinct models.
  createGroup(ids: Iterable<number>): number | null {
    markFloorMutation();
    const members = new Set<number>();
    for (const id of ids) {
      const e = this.items.get(id);
      if (!e) continue;
      members.add(id);
      // Pull in existing group-mates so grouping MERGES rather than splits.
      if (e.groupId != null) {
        for (const o of this.items.values()) if (o.groupId === e.groupId) members.add(o.id);
      }
    }
    if (members.size < 2) return null;
    const gid = this.nextGroup++;
    for (const id of members) this.items.get(id)!.groupId = gid;
    return gid;
  }
  // Clear grouping for every group any of `ids` belongs to.
  ungroup(ids: Iterable<number>): void {
    markFloorMutation();
    const gids = new Set<number>();
    for (const id of ids) {
      const g = this.items.get(id)?.groupId;
      if (g != null) gids.add(g);
    }
    for (const e of this.items.values()) {
      if (e.groupId != null && gids.has(e.groupId)) delete e.groupId;
    }
  }
  private pruneGroups(): void {
    const count = new Map<number, number>();
    for (const e of this.items.values()) {
      if (e.groupId != null) count.set(e.groupId, (count.get(e.groupId) ?? 0) + 1);
    }
    for (const e of this.items.values()) {
      if (e.groupId != null && (count.get(e.groupId) ?? 0) < 2) delete e.groupId;
    }
  }

  // ── Per-model dimension-visibility override ──────────────────────────────────
  // undefined = follow the global toggle; true/false = force show/hide this model.
  dimsOverrideOf(id: number): boolean | undefined {
    return this.items.get(id)?.dimsShow;
  }
  setDimsOverride(ids: Iterable<number>, value: boolean | undefined): void {
    markFloorMutation();
    for (const id of ids) {
      const e = this.items.get(id);
      if (e) e.dimsShow = value;
    }
  }
  // Drop every per-model override so all models follow the global toggle again.
  // Returns true if anything was actually cleared (→ worth an undo step).
  clearDimsOverrides(): boolean {
    markFloorMutation();
    let changed = false;
    for (const e of this.items.values()) {
      if (e.dimsShow !== undefined) {
        e.dimsShow = undefined;
        changed = true;
      }
    }
    return changed;
  }

  // ── Per-model hide ───────────────────────────────────────────────────────────
  isHidden(id: number): boolean {
    return this.items.get(id)?.hidden === true;
  }
  // Hide/show `ids`. Hiding makes the model fully invisible (group.visible=false,
  // unless it's currently ghosted on hover); showing restores it.
  setHidden(ids: Iterable<number>, value: boolean): void {
    markFloorMutation();
    for (const id of ids) {
      const e = this.items.get(id);
      if (!e) continue;
      e.hidden = value;
      if (value) {
        if (!e.ghosted) e.group.visible = false;
      } else {
        if (e.ghosted) this.unghost(e);
        e.group.visible = true;
      }
    }
  }
  // Un-hide every per-model-hidden model. Returns true if anything changed (→ undo
  // step). Used by the global "Models" toggle: turning it off then on reveals all.
  showAll(): boolean {
    markFloorMutation();
    let changed = false;
    for (const e of this.items.values()) {
      if (e.hidden) {
        e.hidden = false;
        if (e.ghosted) this.unghost(e);
        e.group.visible = true;
        changed = true;
      }
    }
    return changed;
  }
  // Drive the hover ghost: the given (hidden) ids should render as faint
  // translucent; every other ghosted model snaps back to fully hidden. Diffs
  // against the current ghost set so material clones happen only on transitions.
  setGhosts(ids: Iterable<number>): void {
    const want = new Set<number>();
    for (const id of ids) if (this.items.get(id)?.hidden) want.add(id);
    for (const e of this.items.values()) {
      if (want.has(e.id)) {
        if (!e.ghosted) this.ghost(e);
      } else if (e.ghosted) {
        this.unghost(e);
        if (e.hidden) e.group.visible = false;
      }
    }
  }
  private ghost(e: Entry): void {
    e.ghosted = true;
    e.group.visible = true;
    e.ghostMats = [];
    e.group.traverse((o) => {
      const mesh = o as THREE.Mesh;
      if (mesh.isMesh && mesh.material) {
        e.ghostMats!.push({ mesh, mat: mesh.material });
        mesh.material = ghostClone(mesh.material);
      }
    });
  }
  private unghost(e: Entry): void {
    if (!e.ghosted) return;
    for (const { mesh, mat } of e.ghostMats ?? []) {
      disposeMat(mesh.material);
      mesh.material = mat;
    }
    e.ghostMats = undefined;
    e.ghosted = false;
  }

  // Re-bake every placement of `modelId` in place — swap its mesh child for a
  // fresh clone while keeping the placement's transform (floor position, yaw,
  // scale). Used when the model is re-saved so the layout reflects the edits.
  // Geometry is NOT disposed here: bakeModelClone's clones share the cached
  // geometry/materials, so disposing one would corrupt the others.
  rebuildModel(modelId: string, makeMesh: () => THREE.Object3D): void {
    for (const e of this.items.values()) {
      if (e.modelId !== modelId) continue;
      for (const child of [...e.group.children]) e.group.remove(child);
      e.group.add(makeMesh());
      e.footprint = undefined; // geometry changed → rebuild snap outline lazily
      // The fresh mesh dropped any ghost material swap; re-assert hidden state.
      e.ghosted = false;
      e.ghostMats = undefined;
      e.group.visible = !e.hidden;
    }
  }

  setSelected(id: number | null): void {
    this.selected = id !== null && this.items.has(id) ? id : null;
  }
  selectedId(): number | null {
    return this.selected;
  }
  // Index of the selected placement in iteration order, or -1 — to re-select the
  // "same" placement after an undo/redo rebuilds placements with fresh ids.
  selectedIndex(): number {
    if (this.selected === null) return -1;
    return [...this.items.keys()].indexOf(this.selected);
  }
  selectByIndex(i: number): void {
    const ids = [...this.items.keys()];
    if (i >= 0 && i < ids.length) this.setSelected(ids[i]);
  }
  groupOf(id: number): THREE.Group | null {
    return this.items.get(id)?.group ?? null;
  }
  setPosition(id: number, x: number, z: number): void {
    markFloorMutation();
    const e = this.items.get(id);
    if (!e) return;
    e.group.position.x = x;
    e.group.position.z = z;
  }
  // Translate a set of placements by (dx,dy,dz) — for a group (marquee) move. dy
  // is non-zero only for a side-elevation move; the caller clamps the group's
  // total descent so the whole selection stops together when the lowest hits the
  // floor (rather than each item clamping on its own and desyncing the group).
  translate(ids: Iterable<number>, dx: number, dy: number, dz: number): void {
    markFloorMutation();
    for (const id of ids) {
      const e = this.items.get(id);
      if (!e) continue;
      e.group.position.x += dx;
      e.group.position.y += dy;
      e.group.position.z += dz;
    }
  }

  // Lowest group-position Y among `ids` (∞ if none) — the caller uses it to cap a
  // group's downward drag at the floor.
  minY(ids: Iterable<number>): number {
    let m = Infinity;
    for (const id of ids) {
      const e = this.items.get(id);
      if (e && e.group.position.y < m) m = e.group.position.y;
    }
    return m;
  }
  // Expand `box` to cover every placement's world AABB (each group holds only
  // its model mesh, so this is clean content — used for layout centering/fit).
  expandBounds(box: THREE.Box3): void {
    for (const e of this.items.values()) {
      this._cbox.setFromObject(e.group);
      if (!this._cbox.isEmpty()) box.union(this._cbox);
    }
  }

  // World-space center of a placement's bounding box (for marquee hit-testing).
  centerWorld(id: number): THREE.Vector3 | null {
    const e = this.items.get(id);
    if (!e) return null;
    this._cbox.setFromObject(e.group);
    return this._cbox.getCenter(new THREE.Vector3());
  }
  // World-space AABB (min/max XYZ) of a placement — for marquee touch-testing.
  worldBox(
    id: number,
  ): { minX: number; minY: number; minZ: number; maxX: number; maxY: number; maxZ: number } | null {
    const e = this.items.get(id);
    if (!e) return null;
    this._cbox.setFromObject(e.group);
    const { min, max } = this._cbox;
    return { minX: min.x, minY: min.y, minZ: min.z, maxX: max.x, maxY: max.y, maxZ: max.z };
  }
  setRotationDeg(id: number, deg: number): void {
    markFloorMutation();
    const e = this.items.get(id);
    if (!e) return;
    e.group.rotation.y = (deg * Math.PI) / 180;
  }
  setScale(id: number, s: number): void {
    markFloorMutation();
    const e = this.items.get(id);
    if (!e) return;
    e.group.scale.setScalar(Math.max(0.01, s));
  }

  info(id: number): PlacementInfo | null {
    const e = this.items.get(id);
    if (!e) return null;
    return {
      id,
      modelId: e.modelId,
      rotationDeg: (e.group.rotation.y * 180) / Math.PI,
      scale: e.group.scale.x,
    };
  }

  // All placements' footprint (bbox XZ) corners — snap targets for measuring.
  private _cbox = new THREE.Box3();
  corners(): { x: number; z: number }[] {
    const out: { x: number; z: number }[] = [];
    for (const e of this.items.values()) {
      this._cbox.setFromObject(e.group);
      const { min, max } = this._cbox;
      out.push(
        { x: min.x, z: min.z },
        { x: max.x, z: min.z },
        { x: min.x, z: max.z },
        { x: max.x, z: max.z },
      );
    }
    return out;
  }

  ids(): number[] {
    return [...this.items.keys()];
  }
  // World bounding-box size (metres) of one placement: width(X), height(Y),
  // depth(Z) — for the selected-model dimension readout. Null if unknown.
  sizeOf(id: number): { w: number; h: number; d: number } | null {
    const e = this.items.get(id);
    if (!e) return null;
    this._cbox.setFromObject(e.group);
    const s = this._cbox.getSize(new THREE.Vector3());
    return { w: s.x, h: s.y, d: s.z };
  }
  // Footprint AABB (world XZ) of one placement, or null. Used for edge-snap.
  aabbOf(
    id: number,
  ): { minX: number; maxX: number; minZ: number; maxZ: number } | null {
    const e = this.items.get(id);
    if (!e) return null;
    this._cbox.setFromObject(e.group);
    return {
      minX: this._cbox.min.x,
      maxX: this._cbox.max.x,
      minZ: this._cbox.min.z,
      maxZ: this._cbox.max.z,
    };
  }
  // Full world AABB (XYZ) of one placement, or null. Used for the per-axis
  // dimension lines (which need the vertical extent for the height reading).
  boundsOf(id: number): {
    minX: number; maxX: number;
    minY: number; maxY: number;
    minZ: number; maxZ: number;
  } | null {
    const e = this.items.get(id);
    if (!e) return null;
    this._cbox.setFromObject(e.group);
    const { min, max } = this._cbox;
    return {
      minX: min.x, maxX: max.x,
      minY: min.y, maxY: max.y,
      minZ: min.z, maxZ: max.z,
    };
  }

  // Measure-snap segments = the object's true footprint OUTLINE, taken from its
  // vertical mesh faces projected onto the floor — so you can snap to a concave
  // face (e.g. the waist of an I-shape), not just a bounding rectangle. Built
  // once per placement in group-LOCAL coords, then transformed to world each
  // call (so move/rotate/scale stay correct).
  private _fpInv = new THREE.Matrix4();
  private _fpA = new THREE.Vector3();
  private _fpB = new THREE.Vector3();
  private _fpC = new THREE.Vector3();
  private _fpAB = new THREE.Vector3();
  private _fpAC = new THREE.Vector3();
  private _fpN = new THREE.Vector3();
  private _fpW = new THREE.Vector3();

  private buildFootprint(e: Entry): { ax: number; az: number; bx: number; bz: number }[] {
    e.group.updateMatrixWorld(true);
    this._fpInv.copy(e.group.matrixWorld).invert(); // world → group-local
    const segs: { ax: number; az: number; bx: number; bz: number }[] = [];
    const pts = [this._fpA, this._fpB, this._fpC];
    e.group.traverse((o) => {
      const m = o as THREE.Mesh;
      if (!m.isMesh || !m.geometry) return;
      const pos = m.geometry.attributes.position as THREE.BufferAttribute | undefined;
      if (!pos) return;
      const index = m.geometry.index;
      const count = index ? index.count : pos.count;
      for (let i = 0; i + 2 < count; i += 3) {
        const i0 = index ? index.getX(i) : i;
        const i1 = index ? index.getX(i + 1) : i + 1;
        const i2 = index ? index.getX(i + 2) : i + 2;
        this._fpA.fromBufferAttribute(pos, i0).applyMatrix4(m.matrixWorld).applyMatrix4(this._fpInv);
        this._fpB.fromBufferAttribute(pos, i1).applyMatrix4(m.matrixWorld).applyMatrix4(this._fpInv);
        this._fpC.fromBufferAttribute(pos, i2).applyMatrix4(m.matrixWorld).applyMatrix4(this._fpInv);
        this._fpAB.subVectors(this._fpB, this._fpA);
        this._fpAC.subVectors(this._fpC, this._fpA);
        this._fpN.crossVectors(this._fpAB, this._fpAC);
        const nlen = this._fpN.length();
        if (nlen < 1e-9 || Math.abs(this._fpN.y) / nlen > 0.3) continue; // keep vertical faces only
        // A vertical triangle projects to a line; take its longest XZ edge.
        let bi = 0;
        let bj = 1;
        let bd = -1;
        for (let x = 0; x < 3; x++) {
          for (let y = x + 1; y < 3; y++) {
            const d = Math.hypot(pts[x].x - pts[y].x, pts[x].z - pts[y].z);
            if (d > bd) { bd = d; bi = x; bj = y; }
          }
        }
        if (bd < 1e-4) continue;
        segs.push({ ax: pts[bi].x, az: pts[bi].z, bx: pts[bj].x, bz: pts[bj].z });
      }
    });
    return segs;
  }

  // World-space footprint outline segments, each tagged with the placement `id`
  // it belongs to — so a measure point that snaps to one can be ANCHORED to that
  // model (and follow it as it moves/rotates/scales).
  footprintEdges(): { ax: number; az: number; bx: number; bz: number; id: number }[] {
    const out: { ax: number; az: number; bx: number; bz: number; id: number }[] = [];
    for (const e of this.items.values()) {
      if (!e.footprint) e.footprint = this.buildFootprint(e);
      e.group.updateMatrixWorld(true);
      const mw = e.group.matrixWorld;
      for (const s of e.footprint) {
        this._fpW.set(s.ax, 0, s.az).applyMatrix4(mw);
        const ax = this._fpW.x;
        const az = this._fpW.z;
        this._fpW.set(s.bx, 0, s.bz).applyMatrix4(mw);
        out.push({ ax, az, bx: this._fpW.x, bz: this._fpW.z, id: e.id });
      }
    }
    return out;
  }

  // World ↔ a placement's local floor frame, for anchoring a measure point to a
  // model. worldToLocal stores the anchor; localToWorld resolves it after a move.
  worldToLocalXZ(id: number, x: number, z: number): { lx: number; lz: number } | null {
    const e = this.items.get(id);
    if (!e) return null;
    e.group.updateMatrixWorld(true);
    this._fpW.set(x, 0, z).applyMatrix4(this._fpInv.copy(e.group.matrixWorld).invert());
    return { lx: this._fpW.x, lz: this._fpW.z };
  }
  localToWorldXZ(id: number, lx: number, lz: number): { x: number; z: number } | null {
    const e = this.items.get(id);
    if (!e) return null;
    e.group.updateMatrixWorld(true);
    this._fpW.set(lx, 0, lz).applyMatrix4(e.group.matrixWorld);
    return { x: this._fpW.x, z: this._fpW.z };
  }

  serialize(): SavedPlacement[] {
    const out: SavedPlacement[] = [];
    for (const e of this.items.values()) {
      const p = e.group.position;
      const q = e.group.quaternion;
      const sp: SavedPlacement = {
        modelId: e.modelId,
        x: p.x,
        y: p.y,
        z: p.z,
        qx: q.x,
        qy: q.y,
        qz: q.z,
        qw: q.w,
        scale: e.group.scale.x,
      };
      if (e.groupId != null) sp.group = e.groupId;
      if (e.dimsShow !== undefined) sp.dimsShow = e.dimsShow;
      if (e.hidden) sp.hidden = true;
      out.push(sp);
    }
    return out;
  }
  clearAll(): void {
    markFloorMutation();
    for (const e of this.items.values()) this.group.remove(e.group);
    this.items.clear();
    this.setSelected(null);
  }

  // Nearest placement under the ray, or null.
  pick(raycaster: THREE.Raycaster): number | null {
    return this.pickHit(raycaster)?.id ?? null;
  }
  // Nearest placement under the ray with its hit distance, so callers can do
  // depth tests (e.g. don't select an object that's behind a wall). Hidden models
  // are excluded by default (you can't click-select what's hidden); pass
  // includeHidden for hover/right-click, which DO need to find a hidden model so
  // it can ghost on hover and offer "Show model" — three skips invisible objects
  // when raycasting, so we briefly un-hide each hidden group around the test.
  pickHit(
    raycaster: THREE.Raycaster,
    includeHidden = false,
  ): { id: number; distance: number } | null {
    let best: { id: number; distance: number } | null = null;
    for (const e of this.items.values()) {
      if (e.hidden && !includeHidden) continue;
      const temp = e.hidden && !e.group.visible;
      if (temp) e.group.visible = true;
      const hits = raycaster.intersectObject(e.group, true);
      if (temp) e.group.visible = false;
      // Only the solid voxel MESH counts as a hit — skip the edge/grid
      // LineSegments. A raycaster's line threshold is ~1 world unit, so including
      // them would let you select the model by clicking the empty area around it.
      const hit = hits.find((h) => (h.object as THREE.Mesh).isMesh);
      if (hit && (best === null || hit.distance < best.distance)) {
        best = { id: e.id, distance: hit.distance };
      }
    }
    return best;
  }
}

// A translucent clone of a (possibly multi-) material for the hover ghost. We
// clone because the bake's materials are shared across every placement of a
// model — mutating one would dim them all. Disposed on un-ghost (see disposeMat).
function ghostClone(
  mat: THREE.Material | THREE.Material[],
): THREE.Material | THREE.Material[] {
  const one = (m: THREE.Material): THREE.Material => {
    const c = m.clone();
    c.transparent = true;
    c.opacity = 0.25;
    c.depthWrite = false;
    return c;
  };
  return Array.isArray(mat) ? mat.map(one) : one(mat);
}
function disposeMat(mat: THREE.Material | THREE.Material[]): void {
  if (Array.isArray(mat)) for (const m of mat) m.dispose();
  else mat.dispose();
}
